import React, { useEffect, useMemo, useRef, useState, type FC } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Modal } from "../../../../components/shared";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import { useListTemplates } from "../../../../queries/dynamicBonuses/useListTemplates";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../../utils/dateService";
import { EMPTY_DISPLAY } from "../constants";
import type { ResultSpecIn, TemplateOut } from "../types";
import OfferPreview from "../controls/OfferPreview";
import customCampaignPlaceholder from "../controls/customCampaignPlaceholder.svg";
import {
  DISPLAY_BANNER_RESULT_TYPE_KEY,
  DEFAULT_CAMPAIGN_TYPE,
  SERIES_CAMPAIGN_TYPE,
  campaignTypeLabel,
  initialCampaignSourceSelection,
  type WizardCampaignType,
} from "./CreateCampaignWizard/toWizardCampaignIn";
import styles from "../stylesDynamicBonuses.module.css";

export type CreateCampaignModalContinue = {
  campaignType: WizardCampaignType;
  template?: TemplateOut;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onContinue: (payload: CreateCampaignModalContinue) => void;
};

type TemplatePreview = {
  result?: ResultSpecIn;
  banner: TemplateOut["default_steps"][number]["banner"];
  layout: TemplateOut["default_steps"][number]["freeform_layout"];
};

type CustomSelectionRow = {
  kind: "custom";
  rowKey: "custom";
};

type TemplateSelectionRow = {
  kind: "template";
  rowKey: string;
  template: TemplateOut;
};

type SelectionRow = CustomSelectionRow | TemplateSelectionRow;

const CUSTOM_SELECTION_ROW: CustomSelectionRow = {
  kind: "custom",
  rowKey: "custom",
};

/** Approximate Ant Design table header height for scroll.y calculation. */
const TABLE_HEADER_HEIGHT = 48;
const TABLE_SCROLL_FALLBACK = 480;

const formatUpdatedAt = (updatedAt: string): string =>
  convertToUserTimezone(updatedAt, "YYYY-MM-DD HH:mm");

const findTemplatePreview = (
  template: TemplateOut,
): TemplatePreview | undefined => {
  for (const step of template.default_steps ?? []) {
    const result =
      step.results.find((candidate) => candidate.type === DISPLAY_BANNER_RESULT_TYPE_KEY) ??
      step.results.find((candidate) => {
        const params = candidate.params ?? {};
        return Boolean(
          step.banner ||
            params.banner_title_key ||
            params.banner_msg_key,
        );
      });

    if (step.banner || result) {
      return { result, banner: step.banner, layout: step.freeform_layout };
    }
  }

  return undefined;
};

const SelectTemplateModal: FC<Props> = ({
  open,
  onClose,
  onContinue,
}) => {
  const { isEnabled: seriesEnabled } = usePamFeature("dynamicBonusesExternal");
  const { isEnabled: internalEnabled } = usePamFeature("dynamicBonusesInternal");
  const [campaignType, setCampaignType] = useState<
    WizardCampaignType | undefined
  >(() =>
    initialCampaignSourceSelection(
      Boolean(seriesEnabled),
      Boolean(internalEnabled),
    ),
  );
  const [sourceError, setSourceError] = useState(false);
  const [tableScrollY, setTableScrollY] = useState(TABLE_SCROLL_FALLBACK);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isError } = useListTemplates();
  const templates = useMemo(() => data ?? [], [data]);
  const isSeries = campaignType === SERIES_CAMPAIGN_TYPE;
  const isPam = campaignType === DEFAULT_CAMPAIGN_TYPE;

  const selectionRows = useMemo<SelectionRow[]>(
    () => [
      CUSTOM_SELECTION_ROW,
      ...templates.map(
        (template): TemplateSelectionRow => ({
          kind: "template",
          rowKey: template.template_id,
          template,
        }),
      ),
    ],
    [templates],
  );

  useEffect(() => {
    if (open) {
      setCampaignType(
        initialCampaignSourceSelection(
          Boolean(seriesEnabled),
          Boolean(internalEnabled),
        ),
      );
      setSourceError(false);
    }
  }, [open, seriesEnabled, internalEnabled]);

  useEffect(() => {
    if (!open || isError) return undefined;

    const el = tableWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const updateScrollY = (height: number) => {
      setTableScrollY(Math.max(120, Math.floor(height - TABLE_HEADER_HEIGHT)));
    };

    updateScrollY(el.getBoundingClientRect().height);

    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (typeof height === "number") updateScrollY(height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, isError, isSeries]);

  const handleSourceSelect = (type: WizardCampaignType) => {
    setCampaignType(type);
    setSourceError(false);
  };

  const handleRowSelect = (row: SelectionRow) => {
    if (!campaignType) {
      setSourceError(true);
      return;
    }
    if (row.kind === "custom") {
      onContinue({ campaignType });
      return;
    }
    onContinue({ campaignType, template: row.template });
  };

  const columns = useMemo<ColumnsType<SelectionRow>>(
    () => [
      {
        title: AppStrings.NAME,
        key: "name",
        width: 440,
        render: (_, row) => {
          if (row.kind === "custom") {
            return (
              <div className={styles.templateSelectionIdentity}>
                <span className={styles.templateSelectionName}>
                  {AppStrings.PAM_DYNAMIC_BONUS_CREATE_CAMPAIGN_CUSTOM}
                </span>
                <span className={styles.templateSelectionDescription}>
                  {AppStrings.PAM_DYNAMIC_BONUS_CREATE_CAMPAIGN_CUSTOM_DESCRIPTION}
                </span>
              </div>
            );
          }

          const description = row.template.description?.trim();

          return (
            <div className={styles.templateSelectionIdentity}>
              <span className={styles.templateSelectionName}>
                {row.template.name}
              </span>
              {description ? (
                <span className={styles.templateSelectionDescription}>
                  {description}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        title: AppStrings.PAM_TEMPLATES_UPDATED,
        key: "updated_at",
        width: 140,
        defaultSortOrder: "descend",
        render: (_, row) =>
          row.kind === "custom"
            ? EMPTY_DISPLAY
            : formatUpdatedAt(row.template.updated_at),
        sorter: (first, second, sortOrder) => {
          if (first.kind === "custom") {
            return sortOrder === "descend" ? 1 : -1;
          }
          if (second.kind === "custom") {
            return sortOrder === "descend" ? -1 : 1;
          }
          return (
            Date.parse(first.template.updated_at) -
            Date.parse(second.template.updated_at)
          );
        },
      },
      {
        title: AppStrings.PREVIEW,
        key: "preview",
        width: 220,
        render: (_, row) => {
          if (row.kind === "custom") {
            return (
              <img
                src={customCampaignPlaceholder}
                alt=""
                className={styles.templateSelectionCustomPreview}
              />
            );
          }

          const preview = findTemplatePreview(row.template);
          if (!preview) {
            return (
              <span className={styles.templateSelectionEmptyPreview}>
                {EMPTY_DISPLAY}
              </span>
            );
          }

          return (
            <div className={styles.templateSelectionPreview}>
              <OfferPreview
                result={preview.result}
                banner={preview.banner}
                layout={preview.layout}
                localizedStrings={row.template.default_localized_strings}
              />
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={AppStrings.PAM_DYNAMIC_BONUS_CREATE_CAMPAIGN_MODAL_TITLE}
      width={960}
      footer={null}
      closable={false}
      className={styles.templateSelectionModal}
      destroyOnClose
    >
      <div className={styles.templateSelectionBody}>
        <div className={styles.templateSelectionTypeField}>
          <span className={styles.templateSelectionTypeLabel}>
            {AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_SOURCE_PICK_LABEL}
          </span>
          <div className={styles.templateSelectionTypeControls}>
            <div
              role="radiogroup"
              aria-label={AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_SOURCE_PICK_LABEL}
              className={styles.pillGroup}
            >
              <FeatureFlagGuard feature="dynamicBonusesExternal" mode="conditional">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSeries}
                  onClick={() => handleSourceSelect(SERIES_CAMPAIGN_TYPE)}
                  className={[
                    styles.pill,
                    styles.pillLarge,
                    isSeries ? styles.pillSelected : "",
                  ]
                    .join(" ")
                    .trim()}
                >
                  {campaignTypeLabel(SERIES_CAMPAIGN_TYPE)}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard feature="dynamicBonusesInternal" mode="conditional">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isPam}
                  onClick={() => handleSourceSelect(DEFAULT_CAMPAIGN_TYPE)}
                  className={[
                    styles.pill,
                    styles.pillLarge,
                    isPam ? styles.pillSelected : "",
                  ]
                    .join(" ")
                    .trim()}
                >
                  {campaignTypeLabel(DEFAULT_CAMPAIGN_TYPE)}
                </button>
              </FeatureFlagGuard>
            </div>
            {sourceError ? (
              <p role="alert" className={styles.templateSelectionTypeError}>
                {AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_SOURCE_REQUIRED}
              </p>
            ) : null}
          </div>
        </div>

        {isSeries && (
          <p className={styles.templateSelectionSeriesHint}>
            {AppStrings.PAM_DYNAMIC_BONUS_BRAZE_MODAL_HINT}
          </p>
        )}

        {isError ? (
          <p className={styles.templateSelectionError}>
            {AppStrings.PAM_TEMPLATES_LOAD_ERROR}
          </p>
        ) : (
          <div ref={tableWrapRef} className={styles.templateSelectionTableWrap}>
            <Table<SelectionRow>
              rowKey="rowKey"
              columns={columns}
              dataSource={selectionRows}
              loading={isLoading}
              pagination={false}
              scroll={{ y: tableScrollY }}
              className={styles.templateSelectionTable}
              locale={{ emptyText: AppStrings.PAM_TEMPLATES_EMPTY }}
              onRow={(row) => ({
                onClick: () => handleRowSelect(row),
              })}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SelectTemplateModal;
